// Types for the Nine Onboarding Program (captain / lead facing APIs).
// All API JSON is camelCase (new-convention endpoints) — no Raw* mirrors needed;
// the backendToUi converter only null-guards and coerces numbers.

import { VisitingCardDetails } from "./VisitingCardObject";

export type ApprovalStatus = "PENDING" | "APPROVED" | "REJECTED";

/** Was a closed "S"|"M"|"L" union; loosened to a generic string once the
 *  "Business Size" dropdown started sourcing its options from the
 *  subscriptions plan catalog (SubscriptionPlan.planCode) instead of a fixed
 *  set — that catalog is itself open-ended by design (see SubscriptionPlan's
 *  own doc comment), so this can't stay a closed union either. */
export type BusinessSize = string;

export type PaymentStatus = "UNPAID" | "PAID" | "NOT_REQUIRED";

/**
 * Structured billing address captured for invoicing. Always required,
 * independent of GSTIN (see DraftBusinessForm.gstin) — line1/city/state/
 * pincode must all be set regardless of whether GSTIN is provided. `country`
 * is fixed to "India": this app is India-only, so it's not user-editable.
 */
export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

/**
 * Which side of the funnel a person works.
 *
 * FORWARD-COMPATIBLE: as of 2026-08-02 the live spec's CaptainSummaryDTO has no
 * `role` field (only `isLead`), so nothing sends this yet. The converter
 * defaults to "CAPTAIN" while it's absent — which is exactly today's behaviour,
 * since anyone who gets a summary back at all is a captain. When the backend
 * ships the field, curator access turns on with no client change.
 *
 * Never branch on this string directly — use canCaptain/canCurate below, so
 * "BOTH" can't be forgotten at a call site.
 */
export type CaptainRole = "CAPTAIN" | "CURATOR" | "BOTH" | "OPERATOR";

export const DEFAULT_CAPTAIN_ROLE: CaptainRole = "CAPTAIN";

/** True when this person may work the sales half of the funnel. */
export function canCaptain(role: CaptainRole): boolean {
  return role === "CAPTAIN" || role === "BOTH";
}

/** True when this person may work the curation half. */
export function canCurate(role: CaptainRole): boolean {
  return role === "CURATOR" || role === "BOTH";
}

/** True when this person may work the media collection / operator half. */
export function canOperate(role: CaptainRole): boolean {
  return role === "OPERATOR";
}

/** Alias for safe backwards compatibility */
export const canCollect = canOperate;

/** True when this person should be shown the operator panel (operators and leads). */
export function canOperateDisplay(role: CaptainRole, isLead: boolean): boolean {
  return canOperate(role) || isLead;
}

/**
 * DISPLAY RULE ONLY: a lead is always shown the curator-facing sections/tiles
 * (My Setups, the Curator Commission stat), regardless of what their
 * nine_captains.role column says. Mirrors a backend rule, not just a frontend
 * one this time -- RoleChecker.isCurator on the server now also counts any
 * lead as a curator (so a lead really can claim a setup and earn curator
 * commission even with role=CAPTAIN), and this keeps the UI in sync with
 * that. Contrast with canCaptain, which has no such lead bypass -- that was
 * a separate, narrower decision that leads are NOT automatically
 * captain-capable everywhere.
 */
export function canCurateDisplay(role: CaptainRole, isLead: boolean): boolean {
  return canCurate(role) || isLead;
}

/** The money fields shared by CaptainSummary and TeamCaptain — everything
 *  needed to work out earned / unsettled for one person. Optional buckets are
 *  "not applicable" (non-lead / non-curator), not ₹0. */
export interface CommissionTotals {
  totalCaptainCommission: number;
  totalLeadCommission?: number | null;
  totalCuratorCommission?: number | null;
  totalSettled: number;
}

/** Lifetime earned: every commission bucket that applies, in one pool — the
 *  pool a settlement draws down. The single definition of "earned" on the
 *  client (summary converter, Earnings tab, Team roster, Settle modal). */
export function earnedOf(totals: CommissionTotals): number {
  return (
    totals.totalCaptainCommission +
    (totals.totalLeadCommission ?? 0) +
    (totals.totalCuratorCommission ?? 0)
  );
}

/** Still owed = earned − settled. Deliberately NOT floored: when more has been
 *  settled than earned (a manual over-settle) the result is negative, and the
 *  UI shows that as-is rather than hiding it behind ₹0. */
export function unsettledOf(totals: CommissionTotals): number {
  // Rounded to paise so float noise (0.1 + 0.2) never shows as ₹0.30000000000000004.
  return Math.round((earnedOf(totals) - totals.totalSettled) * 100) / 100;
}

/** Captain's own dashboard numbers (API: get-captain-summary). */
export interface CaptainSummary {
  captainId: string;
  name: string;
  isLead: boolean;
  /** See CaptainRole. Defaults to "CAPTAIN" until the backend sends it. */
  role: CaptainRole;
  /** The captain's own 50% cut. Present for every captain (even a pure
   *  CURATOR, who simply has ₹0 here) — gate display with canCaptain(role)
   *  rather than on this being present. */
  totalCaptainCommission: number;
  /** The lead's 15% cut. Present only when isLead — absent for non-leads. */
  totalLeadCommission?: number;
  /** The curator's cut, credited when a claimed setup is marked completed.
   *  Present only when canCurate(role) — absent for a pure captain. */
  totalCuratorCommission?: number;
  /** Cumulative amount settled/paid out so far. */
  totalSettled: number;
  /** CLIENT-COMPUTED via unsettledOf — the backend's CaptainSummaryDTO has no
   *  totalUnsettled field (reading one always evaluated to 0). Can be negative
   *  (settled more than earned). */
  totalUnsettled: number;
  /** Captain-side stat (submitted businesses) — meaningless for a pure
   *  curator, who never submits one; gate display with canCaptain(role). */
  totalOnboardings: number;
  pendingOnboardings: number;
}

/** One row in pending_businesses as seen by the submitting captain. */
export interface PendingBusiness {
  pendingId: string;
  businessName: string;
  contactPhone: string;
  /** The `nine_setup` row this draft was created from — lets a captain
   *  viewing Nine Setup find this row's own payment/invoice links. Confirmed
   *  present on the create-pending-business response; optional here since
   *  older rows or other endpoints may omit it. */
  nineSetupId?: string;
  /** Standard/base fee snapshot stored on the row at creation — NOT live
   *  config. Kept for display context ("₹800 of ₹1000"); the captain no
   *  longer derives their price from it via a percentage. */
  onboardingFee: number;
  /** What the captain actually charged. Equals the `chargedAmount` they
   *  typed on submission (the server no longer computes this from a discount). */
  finalFee: number;
  approvalStatus: ApprovalStatus;
  /** Null until approved; then points to the real row in `businesses`. */
  businessId: string | null;
  createdAt: string;
  /** Undefined on rows from before businessSize became required. */
  businessSize?: BusinessSize;
  // ── Payment fields (server-populated; PAID arrives via webhook, the UI
  //    learns of it only by refetching — no push/poll API by design) ──
  /** False when finalFee is ₹0 (captain charged nothing) — no payment UI at all. */
  paymentRequired?: boolean;
  /** Undefined on rows from before the payments rollout → hide the badge. */
  paymentStatus?: PaymentStatus;
  /** Razorpay short link. Null while UNPAID = expired or creation failed. */
  paymentShortUrl?: string | null;
  paymentLinkExpiresAt?: string | null;
  /** Proforma invoice PDF, generated at submission time — present even while
   *  UNPAID. Undefined/null means no invoice yet (e.g. rows from before this
   *  rolled out). */
  invoiceUrl?: string | null;
}

/**
 * Contents of retry-payment-link (API: /api/v1/onboarding/retry-payment-link).
 * Deliberately narrower than PendingBusiness — the endpoint only touches the
 * payment side of an existing row, keyed by pendingId, and never re-validates
 * or re-returns businessName/contactPhone/etc. Callers merge this onto the
 * PendingBusiness row they already have (see OnboardingThunk.regeneratePaymentLink).
 */
export interface PaymentLinkResult {
  paymentRequired: boolean;
  /** Null when generation is still failing (Razorpay down) — caller keeps retrying. */
  shortUrl: string | null;
  expiresAt: string | null;
  paymentStatus: PaymentStatus;
}

/**
 * UI form state for the "Create draft business" modal.
 * Shared fields (name..longitude) mirror CreateBusinessPayload so BusinessForm
 * can be reused; address/contactEmail/chargedAmount are onboarding-only and
 * owned by the modal. Assembled into the wire payload by
 * uiToBackend/OnboardingConvertor — `finalFee` is never sent (the server
 * echoes it back equal to the submitted `chargedAmount`).
 */
export interface DraftBusinessForm {
  name: string;
  listingType: string;
  phone: string;
  description?: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Required key of businessPayload (since payloadVersion 1). */
  address: string;
  contactEmail?: string;
  /** Resolved via phone-number lookup in the modal (see UserByPhoneInput).
   *  Required before submit — the draft can't be created without an owner. */
  ownerId: string;
  /** The exact ₹ amount the captain is charging this business. Must be >= 0
   *  and <= the server's current onboarding fee — that ceiling is NOT known
   *  client-side (no pre-submit endpoint exposes it), so it is enforced only
   *  by the server, which rejects with INVALID_CHARGED_AMOUNT. */
  chargedAmount: number;
  /** Required top-level payload key — NOT nested in businessPayload. Was
   *  S/M/L; now sourced from the subscriptions plan catalog's planCode, so
   *  any non-empty string the "Business Size" dropdown offers is valid
   *  (see BusinessSize's own doc comment above). */
  businessSize: BusinessSize;
  /**
   * The `nine_setup` row this draft came from. Sent as top-level `nineSetupId`.
   *
   * REQUIRED by the server (`VALIDATION_ERROR / "nineSetupId is required"`), so
   * a draft can only be created from a setup row. Still typed optional here
   * because the Onboarding tab's "New draft" button has no setup to supply —
   * that path now fails server-side and needs a product decision.
   */
  setupId?: string;
  /** GST number. Optional and independent of billingAddress — sent inside
   *  businessPayload when provided. */
  gstin?: string;
  /** Always required, independent of gstin. See BillingAddress. */
  billingAddress: BillingAddress;
  /** Required key of businessPayload since payloadVersion 2. See VisitingCardDetails. */
  visitingCard: VisitingCardDetails;
}

/** One captain row in a lead's team view (API: get-captains-by-lead-id). */
export interface TeamCaptain {
  captainId: string;
  /** Auth user id. Present once the roster DTO carries it (backend handover:
   *  "Settlements"); used to match a member to their settlement request. */
  userId?: string;
  name: string;
  phone?: string;
  /** See CaptainRole. Defaults to "CAPTAIN" if the backend omits it. */
  role: CaptainRole;
  /** This member's own 50% cut. Present for every member (even a pure
   *  CURATOR row, who simply has ₹0 here) — gate display with
   *  canCaptain(role) rather than on this being present. */
  totalCaptainCommission: number;
  /** Whether this member is themselves a lead. */
  isLead: boolean;
  /** This member's 15% lead cut — present only if the member is a lead;
   *  null/absent means "not applicable", not ₹0. */
  totalLeadCommission?: number | null;
  /** This member's curator cut — present only when canCurate(role);
   *  null/absent means "not applicable", not ₹0. */
  totalCuratorCommission?: number | null;
  /** Lifetime paid out (returned by get-captains-by-lead-id and settle-captain).
   *  Outstanding = unsettledOf(member). */
  totalSettled: number;
  totalOnboardings: number;
  createdAt: string;
  commissionRate?: number;
}

/** Request body for updating a captain's role and/or commission rate. */
export interface UpdateCaptainPayload {
  role?: CaptainRole;
  commissionRate?: number | null;
}

/** Lead's team. Excludes the lead themselves (their numbers = CaptainSummary).
 *  Team-level totals are deliberately not modelled: the roster screen sums the
 *  payable rows itself so the totals always agree with what it lists. */
export interface CaptainTeam {
  captains: TeamCaptain[];
}

/** Request body for create-captain (lead only). Caller identity = bearer token. */
export interface CreateCaptainPayload {
  /** Auth user id of the person becoming a captain (resolved via phone lookup). */
  userId: string;
  name: string;
  phone?: string;
  /** See CaptainRole. Optional — server defaults to CAPTAIN if omitted, so
   *  this is here only so the lead can explicitly pick CURATOR/BOTH. */
  role?: CaptainRole;
  /** Commission rate for the captain, sent as a decimal (0 to 1). */
  commissionRate?: number | null;
}

/** Contents of the create-captain success envelope. `isLead` is inherited
 *  from TeamCaptain (a freshly created captain is a non-lead). */
export interface CreatedCaptain extends TeamCaptain {
  userId: string;
  leadId: string;
}

/** Request body for settle-captain. Records a payout for one captain/lead. */
export interface SettleCaptainPayload {
  captainId: string;
  /** Amount to settle now. Must be > 0. Added to the captain's totalSettled. */
  amount: number;
}

/** Contents of the settle-captain success envelope — the updated captain with
 *  a definite `totalSettled` (unlike TeamCaptain, where it's unknown). */
export interface SettledCaptain extends CreatedCaptain {
  totalSettled: number;
}

// ── Settlements (payout history + settlement requests) ──
// GENERIC on purpose: a settlement belongs to a user (the payee), not to a
// captain, so any role — lead, captain, curator, or one added later — uses the
// same shapes. Contract handed to the backend in the "Settlements — Backend
// Handover" doc. Tables: settlements and settlement_requests.

/** Who paid. USER = another person (e.g. a lead paying their team), ADMIN =
 *  Nine. SYSTEM = the one-time "opening balance" row standing in for everything
 *  paid before history was tracked. */
export type PayerType = "USER" | "ADMIN" | "SYSTEM";

/** Payout program a settlement draws down. Open-ended on purpose — the backend
 *  adds programs without a client release — so a plain string, with the one
 *  program that exists today named once here. */
export const DEFAULT_SETTLEMENT_PROGRAM = "ONBOARDING";

/** One recorded payout to a person. */
export interface Settlement {
  settlementId: string;
  /** See DEFAULT_SETTLEMENT_PROGRAM. */
  program: string;
  /** Auth user id of the person who was paid. */
  payeeUserId: string;
  amount: number;
  settledAt: string;
  payerType: PayerType;
  /** Display-name snapshot of whoever paid; null for SYSTEM rows. */
  payerName: string | null;
  note: string | null;
  /** Free-text payment reference, e.g. a UPI transaction id. */
  reference: string | null;
}

/** Request body for record-settlement: the payer (a lead, or Admin) records a
 *  payout to one person. Also closes that person's PENDING request, if any. */
export interface RecordSettlementPayload {
  /** Auth user id of the person being paid. */
  payeeUserId: string;
  /** Amount paid now. Must be > 0. */
  amount: number;
  note?: string;
  /** Free-text payment reference, e.g. a UPI transaction id. */
  reference?: string;
}

export type SettlementRequestStatus = "PENDING" | "SETTLED" | "CANCELLED";

export type PayoutMethod = "UPI" | "BANK";

/** Payout details as typed in the request form (all strings, unvalidated).
 *  Turned into the wire PayoutDetails by uiToBackend/OnboardingConvertor. */
export interface PayoutForm {
  method: PayoutMethod;
  upiId: string;
  holder: string;
  account: string;
  ifsc: string;
  phone: string;
}

/** Input to the request-settlement thunk. No amount — the server computes it. */
export interface RequestSettlementInput {
  note?: string;
  payout?: PayoutForm;
}

/** Admin "record payment" input for one request (full amount only). */
export interface SettleRequestPayload {
  /** The amount the admin saw and paid — the server refuses if it changed. */
  expectedAmount: number;
  /** UTR / UPI transaction id. */
  reference?: string;
  note?: string;
}

/** Where the requester wants to be paid, snapshotted on the request so Nine
 *  pays exactly that. UPI needs `upiId`; BANK needs holder, account and ifsc.
 *  `phone` is optional on both. Personal data: held in memory only (the store
 *  is not persisted and is wiped by resetAllState on logout) — never log it or
 *  write it to local/session storage. */
export interface PayoutDetails {
  method: PayoutMethod;
  upiId?: string | null;
  holder?: string | null;
  account?: string | null;
  ifsc?: string | null;
  phone?: string | null;
}

/** A "please pay me" request. The amount is the requester's payable balance at
 *  request time, computed server-side — the client never sends one. A person's
 *  request goes to their payer: a lead for team members, Nine for leads. */
export interface SettlementRequest {
  requestId: string;
  program: string;
  /** Auth user id of the person asking to be paid. */
  payeeUserId: string;
  payeeName: string;
  amount: number;
  status: SettlementRequestStatus;
  /** Who the request is waiting on: another user, or Nine (ADMIN). */
  payerType: Exclude<PayerType, "SYSTEM">;
  payerName: string | null;
  note: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  settlementId: string | null;
  /** Null for requests made before payout details were collected. */
  payout: PayoutDetails | null;
}
